<template>
  <div class="data-extraction-controller">
    <div class="overflow-auto">
      <data-extraction-panel
        :key="reference.id"
        :fields="review.dataExtractionFields"
        :reference="reference"
        :report="report"
        :disabled="disableExtraction"
        :disable-submit="disableSubmit"
        :show-submit="showSubmit"
        :collapse-all="collapseAll"
        @field-value-change="onSaveFieldValue"
        @submit="$emit('submit')"
      />
    </div>
    <v-row v-if="showSubmit" no-gutters class="d-flex flex-grow-0">
      <data-extraction-panel-footer
        class="flex-grow-1"
        :disabled="disableSubmission"
        @submit="$emit('submit', report)"
      />
    </v-row>
  </div>
</template>

<script>
import DataExtractionPanel from '@/components/DataExtraction/DataExtractionPanel';
import DataExtractionPanelFooter from '@/components/DataExtraction/DataExtractionPanelFooter';
import {
  DataExtractionReport,
  Review,
  Reference,
  ReviewMember,
  createDataExtractionValue,
} from '@/models';
import { mapActions } from 'vuex';

export default {
  name: 'DataExtractionController',
  components: { DataExtractionPanel, DataExtractionPanelFooter },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    // Null when the signed-in user is not a member of this review (a super admin, say).
    // They can see the template but have no report of their own to write to.
    reviewMember: {
      type: ReviewMember,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showSubmit: {
      type: Boolean,
      default: false,
    },
    collapseAll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data: () => ({
    debounceTimeouts: {},
    activeSaveTasks: {},
  }),
  computed: {
    report() {
      if (!this.reviewMember) {
        return new DataExtractionReport();
      }

      const existing = this.reference.dataExtractionReports.find(
        (report) => report.reviewer?.id === this.reviewMember.id
      );
      return existing ?? new DataExtractionReport();
    },
    disableExtraction() {
      return this.disabled || !this.reviewMember;
    },
    disableSubmit() {
      return Object.keys(this.debounceTimeouts).length > 0;
    },
    disableSubmission() {
      return this.disableSubmit || this.disableExtraction;
    },
  },
  methods: {
    ...mapActions({
      createReportForReference:
        'dataExtractionReport/createForReference',
      saveFieldValue: 'dataExtractionValue/save',
    }),
    async onSaveFieldValue(fieldValue) {
      const fieldId = fieldValue.field.id;
      clearTimeout(this.debounceTimeouts[fieldId]);

      const getFieldToSave = () => {
        const persistedValue = this.report.values.find(
          (value) => value.field.id === fieldId
        );
        if (!persistedValue) {
          return fieldValue;
        }

        const definedProps = (obj) =>
          Object.fromEntries(
            Object.entries(obj).filter(([, v]) => v !== undefined)
          );
        return createDataExtractionValue({
          ...definedProps(persistedValue),
          ...definedProps(fieldValue),
        });
      };

      this.debounceTimeouts[fieldId] = setTimeout(async () => {
        try {
          if (this.activeSaveTasks[fieldId]) {
            await this.activeSaveTasks[fieldId];
          }
          const report = await this.createReportIfNotExists();

          const fieldToSave = getFieldToSave();
          const saveJob = this.saveFieldValue({
            fieldId: fieldId,
            dataExtractionValue: fieldToSave,
            reportId: report.id,
          });
          this.activeSaveTasks[fieldId] = saveJob;
          await saveJob;
        } finally {
          delete this.debounceTimeouts[fieldId];
          delete this.activeSaveTasks[fieldId];
        }
      }, 500);
    },
    async createReportIfNotExists() {
      if (!this.report.id) {
        return await this.createReportForReference({
          referenceId: this.reference.id,
          reviewerId: this.reviewMember.id,
        });
      }
      return this.report;
    },
  },
};
</script>

<style lang="scss" scoped>
.data-extraction-controller {
  display: grid;
  grid:
    'fields' 1fr
    'footer' auto;
  max-height: 100%;
}
</style>
